<template>
  <component
    v-bind="buttonProps"
    :is="tag"
    class="s-button s-button-text"
    :class="{
      [`s-button-text--action-${actionType}`]: true,
      [`s-button-text--size-${size}`]: true,
      's-button-text--fluid': fluid,
      [`s-button--action-${actionType}`]: true,
      [`s-button--color-${color}`]: true,
      's-button--disabled': disabled,
      's-button--checked': checked,
      's-button--opaque': opaque,
      's-button--rounded': rounded,
      's-button--focused': focused,
      's-button--invalid': invalid,
    }"
    @click="click"
  >
    <SButtonStyles />
    <slot name="start" />
    <span
      class="s-button-text__content"
      :class="[`s-button-text__content--text-align-${textAlign}`]"
    >
      <slot name="prefix" />
      <i v-if="prefixIcon" :class="prefixIcon" />
      <slot />
      <i v-if="suffixIcon" :class="suffixIcon" />
      <slot name="suffix" />
    </span>
    <slot name="end" />
  </component>
</template>

<script setup lang="ts">
import type { UseButtonEmit } from "@/lib/components/logic/atoms/useButton";

import * as useButton from "@/lib/components/logic/atoms/useButton";

import SButtonStyles from "./SButtonStyles.vue";

const props = defineProps(useButton.props);
const emit = defineEmits<UseButtonEmit>();
const { tag, buttonProps, click, suffixIcon, prefixIcon, disabled } =
  useButton.use(props, emit);
</script>

<style lang="postcss">
.s-button-text {
  @apply font-semibold gap-2;

  &__content {
    @apply inline-flex items-center gap-2;

    &--text-align {
      &-left {
        @apply mr-auto text-left;
      }

      &-center {
        @apply mx-auto text-center;
      }

      &-right {
        @apply ml-auto text-right;
      }

      &-between {
        @apply w-full justify-between;
      }
    }
  }

  &--fluid {
    @apply w-full;
  }

  &--action {
    &-primary.s-button--checked {
      @apply ring-2 ring-inset ring-white ring-offset-2;
      --tw-ring-offset-color: var(--button-primary-checked-ring-offset);
    }

    &-primary-soft.s-button--checked {
      @apply ring-2 ring-inset ring-white ring-offset-2;
      --tw-ring-offset-color: var(--button-primary-soft-checked-ring-offset);
    }

    &-tertiary.s-button--checked {
      @apply border-2 border-[var(--button-tertiary-checked-border)] bg-[var(--button-tertiary-checked-bg)];
    }

    &-tertiary-soft.s-button--checked {
      @apply border-[var(--button-tertiary-soft-checked-border)] bg-[var(--button-tertiary-soft-checked-bg)];
    }

    &-secondary.s-button--checked {
      @apply border-2 border-[var(--button-secondary-checked-border)];
    }

    &-quaternary.s-button--checked {
      @apply border-2 border-[var(--button-quaternary-checked-border)];
    }

    &-link {
      color: var(--button-link-color);
      @apply underline;

      &:hover {
        color: var(--button-link-hover-color);
      }
    }
  }

  &--size {
    &-lg {
      @apply min-h-14 px-6 py-2.5 text-base;
    }

    &-md {
      @apply min-h-12 px-5 py-2 text-base;
    }

    &-sm {
      @apply min-h-10 px-4 py-1 text-sm;
    }

    &-xs {
      @apply inline-flex px-2 py-1;
    }

    &-inline {
      @apply -mx-2 -my-1 inline-flex px-2 py-1;
    }
  }
}
</style>
